import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

const ADMIN_ROLES = ['Admin', 'Gerencia', 'Supervisora', 'Auxiliar Administrativo', 'Compras'];

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [subPermissions, setSubPermissions] = useState({});
    const [storeInfo, setStoreInfo] = useState({ assigned_store_id: null, assigned_store_name: null, can_see_all_stores: true });
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('token'));

    const fetchUser = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const userData = response.data.user || response.data;
            setUser(userData);
            setPermissions(response.data.permissions || []);
            // Fetch permissions and store info
            try {
                const permRes = await axios.get(`${API}/my-permissions`, { headers: { Authorization: `Bearer ${token}` } });
                setSubPermissions(permRes.data.sub_permissions || {});
                setStoreInfo({
                    assigned_store_id: permRes.data.assigned_store_id || null,
                    assigned_store_name: permRes.data.assigned_store_name || null,
                    can_see_all_stores: permRes.data.can_see_all_stores !== false
                });
            } catch { /* ignore */ }
        } catch (error) {
            console.error('Error fetching user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token, fetchUser]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API}/auth/login`, { email, password });
            const newToken = response.data.access_token || response.data.token;
            const userData = response.data.user || response.data;
            const userPerms = response.data.permissions || [];
            
            localStorage.setItem('token', newToken);
            setToken(newToken);
            setUser(userData);
            setPermissions(userPerms);
            
            // Fetch sub_permissions immediately after login
            try {
                const permRes = await axios.get(`${API}/my-permissions`, { headers: { Authorization: `Bearer ${newToken}` } });
                setSubPermissions(permRes.data.sub_permissions || {});
                setStoreInfo({
                    assigned_store_id: permRes.data.assigned_store_id || null,
                    assigned_store_name: permRes.data.assigned_store_name || null,
                    can_see_all_stores: permRes.data.can_see_all_stores !== false
                });
            } catch { /* ignore */ }
            
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Error al iniciar sesión' 
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setPermissions([]);
        setSubPermissions({});
        setStoreInfo({ assigned_store_id: null, assigned_store_name: null, can_see_all_stores: true });
    };

    const hasPermission = (moduleSlug) => {
        if (user?.role === 'Admin' || user?.role === 'admin') return true;
        return (user?.module_permissions || []).includes(moduleSlug) || permissions.some(p => p.module_slug === moduleSlug && p.can_view);
    };

    const hasSubPermission = (moduleSlug, subId) => {
        if (user?.role === 'Admin' || user?.role === 'admin') return true;
        // If user doesn't have module permission at all, deny
        if (!(user?.module_permissions || []).includes(moduleSlug)) return false;
        const moduleSubs = subPermissions[moduleSlug];
        // If no sub_permissions defined for this module, allow all (full module access)
        if (!moduleSubs || moduleSubs.length === 0) return true;
        return moduleSubs.includes(subId);
    };

    const canSeeAllStores = () => {
        if (!user) return false;
        return ADMIN_ROLES.includes(user.role) || storeInfo.can_see_all_stores === true || storeInfo.assigned_store_id === 'all';
    };

    const value = {
        user,
        permissions,
        subPermissions,
        storeInfo,
        loading,
        token,
        login,
        logout,
        hasPermission,
        hasSubPermission,
        canSeeAllStores: canSeeAllStores(),
        isAdmin: user?.role === 'Admin' || user?.role === 'admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
